"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { Icon } from "@repo/ui/icon";
import { cn } from "@repo/ui/utils";
import { CommercialTenantCard } from "@/components/cards/commercial-tenant-card";
import { CategoryPills } from "@/components/listing/category-pills";
import { LoadMoreButton } from "@/components/listing/load-more-button";
import { useListingState } from "@/components/listing/use-listing-state";
import type { CommercialTenantListingProps } from "./commercial-tenant-listing.types";
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Search affordance:
 * - Mobile: an always-visible search input styled as a "field" (gray bg,
 *   rounded, icon + placeholder), shown ABOVE the pills.
 * - Desktop: a link-button trigger right-aligned on the pills row. Click
 *   reveals the input below the pills.
 *
 * Mirrors the directory-filter-bar pattern (used by /en/articles) and
 * matches Figma 1407:90937 (desktop) + 1407:90943 (mobile).
 */
export function CommercialTenantListing(t0) {
  const $ = _c(74);
  const {
    headline,
    initialItems,
    initialTotal,
    categories,
    pageSize,
    fetchMore,
    className
  } = t0;
  const tDirectory = useTranslations("directory");
  const tCard = useTranslations("commercialTenantCard");
  let t1;
  if ($[0] !== fetchMore || $[1] !== initialItems || $[2] !== initialTotal || $[3] !== pageSize) {
    t1 = {
      initialItems,
      initialTotal,
      pageSize,
      fetchMore
    };
    $[0] = fetchMore;
    $[1] = initialItems;
    $[2] = initialTotal;
    $[3] = pageSize;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const {
    items,
    selectedCategoryId,
    isPending,
    hasMore,
    setSelectedCategoryId,
    setSearchQuery,
    loadMore
  } = useListingState(t1);
  const [searchInput, setSearchInput] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef(null);
  let t2;
  if ($[5] !== setSearchQuery) {
    t2 = () => {
      setSearchOpen(prev => {
        const next = !prev;
        if (next) {
          requestAnimationFrame(() => searchInputRef.current?.focus());
        } else {
          setSearchInput("");
          setSearchQuery(null);
        }
        return next;
      });
    };
    $[5] = setSearchQuery;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const toggleSearch = t2;
  let t3;
  let t4;
  if ($[7] !== searchInput || $[8] !== setSearchQuery) {
    t3 = () => {
      const id = setTimeout(() => setSearchQuery(searchInput), SEARCH_DEBOUNCE_MS);
      return () => clearTimeout(id);
    };
    t4 = [searchInput, setSearchQuery];
    $[7] = searchInput;
    $[8] = setSearchQuery;
    $[9] = t3;
    $[10] = t4;
  } else {
    t3 = $[9];
    t4 = $[10];
  }
  useEffect(t3, t4);
  let t5;
  if ($[11] !== className) {
    t5 = cn("pt-16 pb-20 desktop:pt-12", className);
    $[11] = className;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== headline) {
    t6 = <h2 className="py-5 typography-mobile-heading-h5 desktop:typography-desktop-heading-h6 text-text-primary">{headline}</h2>;
    $[13] = headline;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = cn("flex items-center gap-2 rounded-xs bg-surface-base-secondary px-4 py-3 desktop:hidden", "focus-within:outline-2 focus-within:outline-border-active");
    t8 = <Icon name="search" className="size-6 shrink-0 text-text-primary" />;
    $[15] = t7;
    $[16] = t8;
  } else {
    t7 = $[15];
    t8 = $[16];
  }
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = event => setSearchInput(event.target.value);
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== tCard) {
    t10 = tCard("searchPlaceholder");
    $[18] = tCard;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = cn("w-full bg-transparent typography-component-sm-regular text-text-primary", "placeholder:text-text-primary focus:outline-none");
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== tDirectory) {
    t12 = tDirectory("search");
    $[21] = tDirectory;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== searchInput || $[24] !== t10 || $[25] !== t12) {
    t13 = <label className={t7}>{t8}<input type="search" value={searchInput} onChange={t9} placeholder={t10} className={t11} aria-label={t12} /></label>;
    $[23] = searchInput;
    $[24] = t10;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== tDirectory) {
    t14 = tDirectory("viewAll");
    $[27] = tDirectory;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== categories || $[30] !== selectedCategoryId || $[31] !== setSelectedCategoryId || $[32] !== t14) {
    t15 = <CategoryPills categories={categories} selectedCategoryId={selectedCategoryId} onSelect={setSelectedCategoryId} allLabel={t14} size="lg" />;
    $[29] = categories;
    $[30] = selectedCategoryId;
    $[31] = setSelectedCategoryId;
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  let t17;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = cn("ml-auto hidden items-center gap-2 py-1 typography-component-md-medium text-text-primary", "transition-colors hover:text-text-secondary", "desktop:flex");
    t17 = <Icon name="search" className="size-5" />;
    $[34] = t16;
    $[35] = t17;
  } else {
    t16 = $[34];
    t17 = $[35];
  }
  let t18;
  if ($[36] !== tDirectory) {
    t18 = tDirectory("search");
    $[36] = tDirectory;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== t18) {
    t19 = <span>{t18}</span>;
    $[38] = t18;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== searchOpen || $[41] !== t19 || $[42] !== toggleSearch) {
    t20 = <button type="button" onClick={toggleSearch} aria-expanded={searchOpen} aria-controls="commercial-tenant-search-panel" className={t16}>{t17}{t19}</button>;
    $[40] = searchOpen;
    $[41] = t19;
    $[42] = toggleSearch;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== t15 || $[45] !== t20) {
    t21 = <div className="flex flex-wrap items-start gap-2">{t15}{t20}</div>;
    $[44] = t15;
    $[45] = t20;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== searchInput || $[48] !== searchOpen || $[49] !== tCard || $[50] !== tDirectory) {
    t22 = searchOpen && <div id="commercial-tenant-search-panel" className="hidden desktop:block"><label className="sr-only" htmlFor="commercial-tenant-search">{tDirectory("search")}</label><input id="commercial-tenant-search" ref={searchInputRef} type="search" value={searchInput} onChange={event_0 => setSearchInput(event_0.target.value)} placeholder={tCard("searchPlaceholder")} className={cn("w-full rounded-xs bg-surface-base-secondary px-4 py-3", "typography-component-sm-regular text-text-primary", "placeholder:text-text-primary focus:outline-2 focus:outline-border-active")} /></div>;
    $[47] = searchInput;
    $[48] = searchOpen;
    $[49] = tCard;
    $[50] = tDirectory;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] !== t13 || $[53] !== t21 || $[54] !== t22) {
    t23 = <div className="flex flex-col gap-6 pb-3">{t13}{t21}{t22}</div>;
    $[52] = t13;
    $[53] = t21;
    $[54] = t22;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== isPending || $[57] !== items || $[58] !== tDirectory) {
    t24 = items.length === 0 && !isPending ? <p className="typography-body-md-regular text-text-secondary">{tDirectory("noResults", {
        label: tDirectory("categories").toLowerCase()
      })}</p> : <ul className={cn("grid grid-cols-2 gap-x-4 gap-y-8", "tablet:grid-cols-2 tablet:gap-x-6", "desktop:grid-cols-4 desktop:gap-y-20", isPending && "opacity-60 transition-opacity")}>{items.map(_temp)}</ul>;
    $[56] = isPending;
    $[57] = items;
    $[58] = tDirectory;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[60] !== hasMore || $[61] !== isPending || $[62] !== loadMore) {
    t25 = hasMore && <LoadMoreButton onClick={loadMore} isPending={isPending} />;
    $[60] = hasMore;
    $[61] = isPending;
    $[62] = loadMore;
    $[63] = t25;
  } else {
    t25 = $[63];
  }
  let t26;
  if ($[64] !== t23 || $[65] !== t24 || $[66] !== t25) {
    t26 = <div className="flex flex-col gap-8 desktop:gap-12">{t23}{t24}{t25}</div>;
    $[64] = t23;
    $[65] = t24;
    $[66] = t25;
    $[67] = t26;
  } else {
    t26 = $[67];
  }
  let t27;
  if ($[68] !== t26 || $[69] !== t6) {
    t27 = <div className="container screen-padding"><div className="flex flex-col">{t6}{t26}</div></div>;
    $[68] = t26;
    $[69] = t6;
    $[70] = t27;
  } else {
    t27 = $[70];
  }
  let t28;
  if ($[71] !== t27 || $[72] !== t5) {
    t28 = <section className={t5} data-testid="commercial-tenant-listing">{t27}</section>;
    $[71] = t27;
    $[72] = t5;
    $[73] = t28;
  } else {
    t28 = $[73];
  }
  return t28;
}
function _temp(item) {
  return <li key={item._id}><CommercialTenantCard title={item.title ?? ""} logoLight={item.logoLight} address={item.address} tradingHours={item.tradingHours} bodyCopy={item.bodyCopy} website={item.website} email={item.email} phone={item.phone} facebook={item.facebook} instagram={item.instagram} /></li>;
}